/**
 * CTA Pulse Outro — starker Abschluss mit pulsierendem Call-to-Action.
 *
 * Klarer Reel-Closer: Logo, eine knackige CTA-Headline, eine pulsierende
 * Action-Pille (mit Pfeil) und die Marken-URL. Ein weicher Ring pulsiert
 * dauerhaft hinter dem Button und zieht den Blick auf die Aktion.
 *
 * Inputs:
 *   text     – CTA-Headline (Default: „Jetzt entdecken")
 *   subtext  – Unterzeile (optional)
 *   extras.button – Button-Label (Default: Marken-URL)
 *   extras.eyebrow – kleines Label (optional)
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, brandLogoImg, brandUrl, clipOutroFade, combineSceneFragments,
  escapeHtml, gsapSetLine, makeTl, typeSize, weights, withAlpha,
  colors as themeColors,
} from "../../reel/sceneShared.js";

export const ctaPulseOutro: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";

  const headline = inputs.text ?? "Jetzt entdecken";
  const sub = inputs.subtext ?? "";
  const eyebrow = inputs.extras?.eyebrow != null ? String(inputs.extras.eyebrow) : "";
  const cleanUrl = brandUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
  const button = inputs.extras?.button != null ? String(inputs.extras.button) : cleanUrl;

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);
  const headPx = isVertical ? lg * 0.96 : lg * 0.88;
  const subPx = isVertical ? md * 0.54 : md * 0.48;
  const btnPx = isVertical ? md * 0.6 : md * 0.52;
  const eyePx = sm * 0.62;
  const logoPx = isVertical ? 132 : 116;

  const wrapId = `${clipPrefix}-wrap`;
  const logoId = `${clipPrefix}-logo`;
  const eyeId = `${clipPrefix}-eye`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const btnId = `${clipPrefix}-btn`;
  const ringId = `${clipPrefix}-ring`;

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "spotlight", accent });

  const arrow = `<svg width="${Math.round(btnPx * 0.9)}" height="${Math.round(btnPx * 0.9)}" viewBox="0 0 24 24" fill="none" stroke="${themeColors.bg}" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;"><path d="M5 12h14M13 6l6 6-6 6"/></svg>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 26 : 22}px;text-align:center;padding:${isVertical ? "120px 80px" : "110px 150px"};overflow:hidden;">
    ${brandLogoImg({ id: logoId, sizePx: logoPx })}
    ${eyebrow ? `<div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyePx}px;letter-spacing:7px;text-transform:uppercase;color:${accent};">${escapeHtml(eyebrow)}</div>` : ""}
    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headPx}px;line-height:1.02;letter-spacing:-2.2px;color:${themeColors.fg};text-wrap:balance;text-shadow:0 0 60px ${withAlpha(accent, 0.4)};max-width:${isVertical ? 900 : 1200}px;">${escapeHtml(headline)}</div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subPx}px;letter-spacing:-0.2px;line-height:1.3;color:${withAlpha(themeColors.fg, 0.78)};max-width:${isVertical ? 820 : 1040}px;text-wrap:balance;">${escapeHtml(sub)}</div>` : ""}
    <div style="position:relative;display:flex;align-items:center;justify-content:center;margin-top:${isVertical ? 14 : 10}px;">
      <div id="${ringId}" style="position:absolute;inset:-10px;border-radius:9999px;border:2px solid ${withAlpha(accent, 0.6)};pointer-events:none;"></div>
      <div id="${btnId}" style="position:relative;display:inline-flex;align-items:center;gap:${isVertical ? 18 : 16}px;padding:${isVertical ? "20px 40px" : "18px 38px"};border-radius:9999px;background:linear-gradient(120deg, ${accent} 0%, ${withAlpha(themeColors.secondary, 0.95)} 100%);color:${themeColors.bg};font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${btnPx}px;letter-spacing:-0.5px;box-shadow:0 0 50px ${withAlpha(accent, 0.6)}, 0 20px 50px rgba(0,0,0,0.4);">
        <span>${escapeHtml(button)}</span>${arrow}
      </div>
    </div>
  </div>`;

  const init = [
    gsapSetLine(`#${logoId}`, { opacity: 0, y: 20, scale: 0.8 }),
    eyebrow ? gsapSetLine(`#${eyeId}`, { opacity: 0, y: -6 }) : "",
    gsapSetLine(`#${headId}`, { opacity: 0, y: 22 }),
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 16 }) : "",
    gsapSetLine(`#${btnId}`, { opacity: 0, y: 24, scale: 0.9 }),
    gsapSetLine(`#${ringId}`, { opacity: 0, scale: 0.8 }),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${logoId}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "back.out(1.7)" }, 0.1),
    eyebrow ? t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" }, 0.28) : "",
    t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "power3.out" }, 0.32),
    sub ? t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" }, 0.5) : "",
    t.to(`#${btnId}`, { opacity: 1, y: 0, scale: 1, duration: 0.55, ease: "back.out(2.0)" }, 0.62),
  ].filter((s): s is string => Boolean(s));

  // Dauerhafter, dezenter Puls am Button + Ring (läuft bis kurz vor Outro).
  const pulseStart = 1.0;
  const pulseEnd = Math.max(pulseStart + 0.6, clipDurationSec - 0.5);
  if (pulseEnd - pulseStart > 0.6) {
    const cycles = Math.max(1, Math.floor((pulseEnd - pulseStart) / 1.2));
    tweens.push(t.to(`#${btnId}`, { scale: 1.05, duration: 0.6, ease: "sine.inOut", yoyo: true, repeat: cycles * 2 - 1 }, pulseStart));
    tweens.push(t.set(`#${ringId}`, { opacity: 1 }, pulseStart));
    for (let c = 0; c < cycles; c++) {
      const at = pulseStart + c * 1.2;
      tweens.push(t.fromTo(`#${ringId}`, { opacity: 0.6, scale: 0.85 }, { opacity: 0, scale: 1.25, duration: 1.1, ease: "power2.out" }, at));
    }
  }

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 8));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
